import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import Button from '../ui/Button';
import Footer from '../Footer';
import { CookiePreferencesModal } from './CookiePreferencesModal';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import SettingsSubnav from '../settings/SettingsSubnav';
import { clearSpotDrafts } from '../../services/collectorDrafts';

const PrivacyCenterPage: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [cookieOpen, setCookieOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [exportBusy, setExportBusy] = useState(false);

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <PageBackLink label="Back to Settings" to="/settings" className="mb-4" />
        <PageHeader
          eyebrow="Settings"
          title="Privacy and data"
          description="Legal documents, cookie choices, marketing preferences, export, and account deletion."
        />
        <div className="mt-6">
          <SettingsSubnav />
        </div>
        <div className="mt-2 space-y-3">
          {[
            ['Privacy Policy', '/privacy'],
            ['Terms of Service', '/terms'],
            ['Community Guidelines', '/community-guidelines'],
            ['Cookie Policy', '/cookies'],
            ['Copyright / DMCA', '/copyright'],
          ].map(([label, to]) => (
            <Link
              key={to}
              to={to}
              className="block rounded-lg border border-white/[0.08] bg-[#0c0c0c] px-4 py-3 text-sm text-gray-200 hover:border-white/20"
            >
              {label}
            </Link>
          ))}
          <button
            type="button"
            className="w-full text-left rounded-lg border border-white/[0.08] bg-[#0c0c0c] px-4 py-3 text-sm text-gray-200 hover:border-white/20 min-h-[44px]"
            onClick={() => setCookieOpen(true)}
          >
            Cookie preferences
          </button>
        </div>

        {isAuthenticated ? (
          <div className="mt-8 space-y-3">
            <h2 className="font-display text-xl text-white">Your data</h2>
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              loading={exportBusy}
              onClick={() => {
                setExportBusy(true);
                setStatus('');
                void apiService
                  .exportAccountData()
                  .then((res: any) => {
                    const blob = new Blob([JSON.stringify(res.data || res, null, 2)], {
                      type: 'application/json',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'bilt-account-export.json';
                    a.click();
                    URL.revokeObjectURL(url);
                    setStatus('Export downloaded.');
                  })
                  .catch((err: any) => setStatus(err?.message || 'Export failed.'))
                  .finally(() => setExportBusy(false));
              }}
            >
              Download account data
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => {
                void apiService
                  .submitPrivacyRequest({ request_type: 'marketing_opt_out' })
                  .then(() => setStatus('Marketing email opt-out recorded.'));
              }}
            >
              Opt out of marketing email
            </Button>
            <Link className="block py-3 text-amber-200 underline" to="/passport/preferences">Private collection preferences and export</Link>
            <Button variant="danger" onClick={() => setDeleteOpen(true)}>Delete my account</Button>
            {deleteOpen && <form className="rounded-xl border border-red-400/30 p-4 space-y-4" onSubmit={event => {
              event.preventDefault(); if (!user || !deleteConfirmed) return;
              const owner = Number(user.id); setDeleteBusy(true); setStatus('');
              void (async () => {
                try {
                  const response = await apiService.makeRequest('/account_delete.php', { method: 'POST', body: JSON.stringify({ password }) });
                  if (!response.success) throw new Error(response.error || 'Account deletion failed');
                  let localRemoved = true;
                  try { await clearSpotDrafts(owner); } catch { localRemoved = false; }
                  setPassword(''); await logout();
                  setStatus(localRemoved ? 'Your account and private collection have been deleted.' : 'Your account is deleted. Local drafts could not be removed; clear this app’s storage on this device.');
                  setDeleteOpen(false);
                } catch (error) { setStatus(error instanceof Error ? error.message : 'Account deletion failed'); }
                finally { setDeleteBusy(false); }
              })();
            }}>
              <p className="text-sm text-zinc-300">This permanently removes your profile, personal build content, private photo spots, memories, and drafts on this device, and cancels active Bilt Stripe subscriptions. Payment and event financial records are retained for accounting, disputes, and legal obligations.</p>
              <label className="block">Confirm your password<input className="input mt-2" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} /></label>
              <label className="flex gap-3"><input type="checkbox" required checked={deleteConfirmed} onChange={event => setDeleteConfirmed(event.target.checked)} />I understand this cannot be undone.</label>
              <Button type="submit" variant="danger" loading={deleteBusy} disabled={!deleteConfirmed}>Permanently delete account</Button>
              <Button variant="secondary" disabled={deleteBusy} onClick={() => { setDeleteOpen(false); setPassword(''); setDeleteConfirmed(false); }}>Keep my account</Button>
            </form>}

          </div>
        ) : (
          <p className="mt-8 text-sm text-gray-500">
            <Link to="/login" className="text-orange-300">
              Sign in
            </Link>{' '}
            to export data or manage marketing preferences.
          </p>
        )}
        {status ? (
          <p className="mt-4 text-sm text-emerald-300" role="status">
            {status}
          </p>
        ) : null}
      </div>
      <CookiePreferencesModal isOpen={cookieOpen} onClose={() => setCookieOpen(false)} />
      <Footer />
    </PageShell>
  );
};

export default PrivacyCenterPage;
